<template>
  <div class="leaderboard-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('leaderboard.title') }}</h1>
        <p class="page-subtitle">{{ t('leaderboard.subtitle') }}</p>
      </div>
    </section>

    <!-- Leaderboard Content -->
    <section class="leaderboard-content">
      <div class="container">
        <!-- Game Selection -->
        <div class="game-filter">
          <button
            v-for="game in gameOptions"
            :key="game.code"
            :class="{ active: selectedGame === game.code }"
            @click="selectedGame = game.code"
            class="filter-btn"
          >
            {{ game.name }}
          </button>
        </div>

        <!-- Stats Overview -->
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-label">{{ t('leaderboard.highScore') }}</div>
            <div class="stat-value">{{ getGameStats().highScore }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('leaderboard.totalGames') }}</div>
            <div class="stat-value">{{ getGameStats().totalGames }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('leaderboard.avgScore') }}</div>
            <div class="stat-value">{{ getGameStats().avgScore }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('leaderboard.winRate') }}</div>
            <div class="stat-value">{{ getGameStats().winRate }}%</div>
          </div>
        </div>

        <!-- Game History -->
        <div class="history-section">
          <h2>{{ t('leaderboard.gameHistory') }}</h2>
          <div v-if="getGameStats().scores.length > 0" class="scores-list">
            <div
              v-for="(score, index) in getGameStats().scores"
              :key="index"
              class="score-item"
            >
              <div class="score-rank">{{ index + 1 }}</div>
              <div class="score-info">
                <div class="score-time">{{ formatDate(score.date) }}</div>
                <div class="score-mode" v-if="score.mode">{{ score.mode }}</div>
              </div>
              <div class="score-points">{{ score.score }}</div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p>{{ t('leaderboard.noScores') }}</p>
            <button class="btn-play" @click="goToGame">{{ t('buttons.play') }}</button>
          </div>
        </div>

        <!-- Reset Button -->
        <div class="action-buttons">
          <button class="btn-reset" @click="resetScores">{{ t('buttons.reset') }}</button>
          <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()
const selectedGame = ref('poker')

const gameOptions = [
  { code: 'poker', name: 'Word Poker' },
  { code: 'sudoku', name: 'Sudoku' },
  { code: 'riddles', name: 'Brain Teasers' }
]

const getGameStats = () => {
  const storageKey = `game_scores_${selectedGame.value}`
  const scores = JSON.parse(localStorage.getItem(storageKey) || '[]')

  if (scores.length === 0) {
    return {
      highScore: 0,
      totalGames: 0,
      avgScore: 0,
      winRate: 0,
      scores: []
    }
  }

  const highScore = Math.max(...scores.map(s => s.score))
  const totalGames = scores.length
  const avgScore = Math.round(scores.reduce((sum, s) => sum + s.score, 0) / totalGames)
  const winRate = Math.round(
    (scores.filter(s => s.score > 0).length / totalGames) * 100
  )

  return {
    highScore,
    totalGames,
    avgScore,
    winRate,
    scores: scores.sort((a, b) => new Date(b.date) - new Date(a.date))
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const resetScores = () => {
  if (confirm(t('leaderboard.confirmReset'))) {
    const storageKey = `game_scores_${selectedGame.value}`
    localStorage.removeItem(storageKey)
    selectedGame.value = selectedGame.value // trigger reactivity
  }
}

const goToGame = () => {
  window.location.hash = `#/games/${selectedGame.value}`
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.leaderboard-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.leaderboard-content {
  padding: var(--space-4xl) var(--space-base);
}

.container {
  max-width: 1000px;
  margin: 0 auto;
}

/* Game Filter */
.game-filter {
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-bottom: var(--space-3xl);
  flex-wrap: wrap;
}

.filter-btn {
  padding: var(--space-sm) var(--space-lg);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
}

.filter-btn:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
}

.filter-btn.active {
  background: var(--gradient-primary);
  color: white;
  border-color: var(--color-primary-orange);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-xl);
  margin-bottom: var(--space-3xl);
}

.stat-card {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  text-align: center;
  border: 1px solid var(--color-border);
  transition: all var(--transition-base) var(--ease-in-out));
}

.stat-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.stat-label {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
  font-weight: var(--font-weight-semibold);
}

.stat-value {
  font-size: clamp(24px, 6vw, 40px);
  color: var(--color-primary-orange);
  font-weight: var(--font-weight-bold);
}

/* History Section */
.history-section {
  margin-bottom: var(--space-3xl);
}

.history-section h2 {
  font-size: var(--font-size-h4);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-xl) 0;
  font-weight: var(--font-weight-bold);
}

.scores-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.score-item {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding: var(--space-lg);
  background: var(--color-bg-card);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  transition: all var(--transition-base) var(--ease-in-out));
}

.score-item:hover {
  background: var(--color-bg-light);
  box-shadow: var(--shadow-sm);
}

.score-rank {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  color: white;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

.score-info {
  flex: 1;
}

.score-time {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
}

.score-mode {
  font-size: var(--font-size-tiny);
  color: var(--color-text-tertiary);
  margin-top: var(--space-xs);
}

.score-points {
  font-size: clamp(18px, 4vw, 24px);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-orange);
  min-width: 60px;
  text-align: right;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--space-4xl) var(--space-2xl);
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 2px dashed var(--color-border);
}

.empty-state p {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-xl) 0;
}

.btn-play {
  padding: var(--space-md) var(--space-2xl);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-play:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: var(--space-lg);
  justify-content: center;
  flex-wrap: wrap;
}

.btn-reset,
.btn-back {
  padding: var(--space-md) var(--space-2xl);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
  border: none;
}

.btn-reset {
  background: var(--color-danger);
  color: white;
}

.btn-reset:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}

.btn-back {
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .leaderboard-content {
    padding: var(--space-2xl) var(--space-base);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  .stat-card {
    padding: var(--space-lg);
  }

  .score-item {
    gap: var(--space-md);
    padding: var(--space-base);
  }

  .score-rank {
    width: 32px;
    height: 32px;
    font-size: var(--font-size-small);
  }

  .score-time {
    font-size: var(--font-size-tiny);
  }

  .action-buttons {
    gap: var(--space-md);
  }

  .btn-reset,
  .btn-back {
    flex: 1;
    padding: var(--space-base) var(--space-lg);
    font-size: var(--font-size-small);
  }
}
</style>
